import { computed, ref } from 'vue';

import axios from '@/plugins/axios.js';

/**
 * useQuizActivity — inline quiz-taking engine (corporate activity page).
 *
 * Standalone, raw-axios orchestration of the quiz-attempt lifecycle, WITHOUT the
 * router navigation the legacy `quiz-attempt/QuizAttempt.vue` flow uses:
 *
 *   start()  → POST /api/quiz-attempts            (create attempt)
 *            → GET  /api/quiz-attempt-questions   (load this attempt's questions)
 *   select() → POST /api/quiz-attempt-answers     (persist a selection, finalize=false)
 *   submit() → POST /api/quiz-attempt-answers     (finalize each answered question)
 *            → PUT  /api/quiz-attempts/{id}       (end the attempt)
 *            → GET  /api/quiz-attempts/{id}/review (score, if review allowed)
 *
 * The consuming component owns metadata (quizVersionId, quizActivityId, courseId,
 * userId, type) and passes it into start()/submit(); this composable owns only the
 * take-and-submit reactive state. Question types: 'true_false' / 'multiple_choice'
 * are single-answer, 'multiple_choice_multiple_answer' is multi, 'free_form' is text.
 *
 * Out of scope by design (corporate does NOT use these): timer / time-limit, navigation
 * lockdown, waiting-room / force-start (instructor-led). Known limitation: free_form
 * saves always POST a fresh answer record (no PUT-update de-dup) — fine for corporate.
 */

export const QUIZ_PHASES = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    IN_PROGRESS: 'in-progress',
    SUBMITTING: 'submitting',
    REVIEW: 'review',
    ERROR: 'error',
});

// Laravel resources sometimes wrap the payload in `{ data }`, sometimes not.
const unwrap = response => response?.data?.data ?? response?.data ?? null;

const sortByOrder = (list, key) => [...(list ?? [])].sort((a, b) => (a[key] ?? 0) - (b[key] ?? 0));

export default function useQuizActivity() {
    const phase = ref(QUIZ_PHASES.IDLE);
    const error = ref(null);
    // Set when an interim/draft/finalize answer POST fails: { qaqId, error }; cleared on
    // the next successful save. Lets the template surface a per-question "couldn't save".
    const saveError = ref(null);
    const attempt = ref(null);
    const questions = ref([]);
    const index = ref(0);
    // selections keyed by quiz_attempt_question id → answerId (single) | answerId[] (multi) | string (free_form)
    const selections = ref({});
    const score = ref(null);
    const reviewQuestions = ref([]);
    const submittedAt = ref(null);
    // Set when start() is blocked because another quiz attempt is active elsewhere:
    // { activity_id, course_id, title }.
    const blockedByAttempt = ref(null);

    const isLaunching = computed(() => phase.value === QUIZ_PHASES.LOADING);
    const isSubmitting = computed(() => phase.value === QUIZ_PHASES.SUBMITTING);
    const started = computed(() =>
        [QUIZ_PHASES.IN_PROGRESS, QUIZ_PHASES.SUBMITTING, QUIZ_PHASES.REVIEW].includes(phase.value)
    );
    const inReview = computed(() => phase.value === QUIZ_PHASES.REVIEW);
    const hasError = computed(() => phase.value === QUIZ_PHASES.ERROR);

    const total = computed(() => questions.value.length);
    const currentQuestion = computed(() => questions.value[index.value] ?? null);
    const isFirst = computed(() => index.value <= 0);
    const isLast = computed(() => index.value >= total.value - 1);

    const scorePercent = computed(() =>
        score.value?.total_score == null ? null : Math.round(score.value.total_score * 1000) / 10
    );

    // A prior completed attempt's result is loaded (drives the "past results" summary).
    const hasPastResult = computed(() => submittedAt.value != null);

    // Questions answered fully correctly (needs is_correct + provided answers in the
    // /review payload — gated by the quiz's show_*_in_review settings; 0 when hidden).
    const correctCount = computed(
        () =>
            reviewQuestions.value.filter(qaq => {
                const correctIds = (qaq.question?.answers ?? []).filter(a => a.is_correct).map(a => a.id);
                if (!correctIds.length) return false;
                const selectedIds = (qaq.quiz_attempt_answers ?? []).map(a => a.answer_id);
                if (correctIds.length !== selectedIds.length) return false;
                const selected = new Set(selectedIds);
                return correctIds.every(id => selected.has(id));
            }).length
    );
    const reviewTotal = computed(() => reviewQuestions.value.length);

    // ---- type helpers (operate on a normalized quiz-attempt-question) ----
    const typeOf = qaq => qaq?.question?.type ?? qaq?.type ?? null;
    const isMultipleAnswer = qaq => typeOf(qaq) === 'multiple_choice_multiple_answer';
    const isFreeForm = qaq => typeOf(qaq) === 'free_form';

    function normalize(rawQuestions) {
        return sortByOrder(rawQuestions, 'order').map(qaq => ({
            id: qaq.id, // quiz_attempt_question_id — the submission key
            order: qaq.order ?? 0,
            question: {
                ...qaq.question,
                answers: sortByOrder(qaq.question?.answers, 'display_order'),
            },
            answerAttempts: qaq.quiz_attempt_answers ?? [],
        }));
    }

    // Restore any answers already saved against this attempt (resume support).
    function hydrateSelections(items) {
        const next = {};
        for (const qaq of items) {
            const prior = qaq.answerAttempts;
            if (!prior?.length) continue;

            if (isFreeForm(qaq)) {
                next[qaq.id] = prior[0].answer_body ?? '';
            } else if (isMultipleAnswer(qaq)) {
                next[qaq.id] = prior.map(a => a.answer_id);
            } else {
                next[qaq.id] = prior[0].answer_id;
            }
        }
        selections.value = next;
    }

    function isSelected(qaq, answerId) {
        const sel = selections.value[qaq?.id];
        return Array.isArray(sel) ? sel.includes(answerId) : sel === answerId;
    }

    function freeFormText(qaqId) {
        const sel = selections.value[qaqId];
        return typeof sel === 'string' ? sel : '';
    }

    function setFreeForm(qaq, value) {
        selections.value = { ...selections.value, [qaq.id]: value };
    }

    function answered(qaq) {
        const sel = selections.value[qaq.id];
        if (isFreeForm(qaq)) return !!(sel && String(sel).trim());
        return Array.isArray(sel) ? sel.length > 0 : sel != null;
    }

    function buildAnswerPayload(qaq, finalize) {
        if (isFreeForm(qaq)) {
            return { quiz_attempt_question_id: qaq.id, answer_body: freeFormText(qaq.id), finalize };
        }

        const sel = selections.value[qaq.id];
        const ids = Array.isArray(sel) ? sel : sel == null ? [] : [sel];
        const answers = ids.map(id => {
            const answer = qaq.question.answers.find(a => a.id === id);
            return { ...answer, answer_id: id, selected: true };
        });

        return { quiz_attempt_question_id: qaq.id, answers, finalize };
    }

    // Per-question POST serialization. The API deletes-and-recreates every answer for a
    // question on each POST, so overlapping writes for the same qaq can race (a late draft
    // could clobber a finalized row). Chain writes per qaq.id so they run in order.
    const persistQueues = new Map();

    async function persist(qaq, finalize) {
        const previous = persistQueues.get(qaq.id) ?? Promise.resolve();

        const run = previous
            .catch(() => {}) // isolate this write from an earlier failure in the same queue
            .then(async () => {
                try {
                    await axios.post('/api/quiz-attempt-answers', buildAnswerPayload(qaq, finalize));
                    saveError.value = null;
                } catch (e) {
                    saveError.value = { qaqId: qaq.id, error: e };
                    throw e;
                }
            });

        persistQueues.set(qaq.id, run);

        try {
            await run;
        } finally {
            if (persistQueues.get(qaq.id) === run) {
                persistQueues.delete(qaq.id);
            }
        }
    }

    // Await any in-flight per-question writes so a late draft save can't land after
    // (and overwrite) the finalized answers on submit.
    async function flushPendingPersists() {
        await Promise.allSettled([...persistQueues.values()]);
    }

    // ---- public actions ----
    async function start({ quizVersionId, quizActivityId, userId, type, preview = false }) {
        phase.value = QUIZ_PHASES.LOADING;
        error.value = null;
        blockedByAttempt.value = null;
        // Clear any prior-attempt result so a new attempt starts clean.
        score.value = null;
        reviewQuestions.value = [];
        submittedAt.value = null;

        try {
            const activityKey = type === 'survey' ? 'survey_activity_id' : 'quiz_activity_id';
            const response = await axios.post('/api/quiz-attempts', {
                quiz_version_id: quizVersionId,
                user_id: userId,
                is_preview: preview,
                [activityKey]: quizActivityId,
            });

            attempt.value = unwrap(response);
            await loadQuestions(attempt.value.id);
            index.value = 0;
            phase.value = QUIZ_PHASES.IN_PROGRESS;
        } catch (e) {
            // Another quiz attempt is active elsewhere — surface it (with a deep link)
            // on the Begin screen rather than a generic error.
            const active = e?.response?.data?.meta?.active_attempt;
            if (active) {
                blockedByAttempt.value = active;
                phase.value = QUIZ_PHASES.IDLE;
                return;
            }
            phase.value = QUIZ_PHASES.ERROR;
            error.value = e;
            throw e;
        }
    }

    async function loadQuestions(attemptId) {
        const response = await axios.get(`/api/quiz-attempt-questions?filter[quiz_attempt_id]=${attemptId}`);
        const items = normalize(response?.data?.data ?? []);
        questions.value = items;
        hydrateSelections(items);
    }

    // Find this user's latest non-preview attempt for the activity (null if none).
    async function findLatestAttempt({ quizActivityId, userId, type }) {
        const key = type === 'survey' ? 'survey_activity_id' : 'quiz_activity_id';
        const response = await axios.get(
            `/api/quiz-attempts?filter[user_id]=${userId}&filter[${key}]=${quizActivityId}`
        );
        const rows = (response?.data?.data ?? []).filter(a => !a.is_preview);
        if (!rows.length) return null;
        return rows.reduce((latest, row) => (row.id > latest.id ? row : latest));
    }

    // On load: resume an in-progress attempt (auto-enter questions at the first
    // unanswered) or render the review for a completed one. No attempt → stay idle
    // (the Begin screen). Best-effort: failures fall back to idle.
    async function resume({ quizActivityId, userId, type, allowRetake = false }) {
        if (!quizActivityId || !userId || phase.value !== QUIZ_PHASES.IDLE) return;

        phase.value = QUIZ_PHASES.LOADING;
        try {
            const found = await findLatestAttempt({ quizActivityId, userId, type });
            if (!found) {
                phase.value = QUIZ_PHASES.IDLE;
                return;
            }

            attempt.value = found;

            if (found.end_time) {
                // Completed: load its result. With attempts remaining, land on the Begin
                // screen (the loaded result shows as "past results" below it); otherwise
                // show the full review screen.
                submittedAt.value = found.end_time;
                try {
                    await fetchScore(found.id);
                } catch {
                    score.value = null;
                    reviewQuestions.value = [];
                }
                phase.value = allowRetake ? QUIZ_PHASES.IDLE : QUIZ_PHASES.REVIEW;
                return;
            }

            // In-progress → resume the question view at the first unanswered question.
            await loadQuestions(found.id);
            const firstUnanswered = questions.value.findIndex(qaq => !answered(qaq));
            index.value = firstUnanswered === -1 ? Math.max(0, total.value - 1) : firstUnanswered;
            phase.value = QUIZ_PHASES.IN_PROGRESS;
        } catch {
            // Couldn't resolve a prior attempt — fall back to the Begin screen.
            phase.value = QUIZ_PHASES.IDLE;
        }
    }

    // Persist a selection immediately (interim save, finalize=false).
    async function select(qaq, answerId) {
        if (isMultipleAnswer(qaq)) {
            const current = Array.isArray(selections.value[qaq.id]) ? [...selections.value[qaq.id]] : [];
            const at = current.indexOf(answerId);
            if (at >= 0) current.splice(at, 1);
            else current.push(answerId);
            selections.value = { ...selections.value, [qaq.id]: current };
        } else {
            selections.value = { ...selections.value, [qaq.id]: answerId };
        }

        // Interim save: the failure is already recorded in saveError for the UI, so don't let the
        // click handler reject (submit() keeps persist()'s throw path to detect a failed finalize).
        await persist(qaq, false).catch(() => {});
    }

    // Persist a free_form draft (call on blur).
    async function persistDraft(qaq) {
        if (answered(qaq)) await persist(qaq, false).catch(() => {});
    }

    function next() {
        if (!isLast.value) index.value += 1;
    }

    function prev() {
        if (!isFirst.value) index.value -= 1;
    }

    async function submit({ userId, quizActivityId, endTime, reviewAllowed = false }) {
        phase.value = QUIZ_PHASES.SUBMITTING;
        error.value = null;

        try {
            // Wait for any in-flight draft/interim writes so they can't land after
            // (and clobber) the finalized answers we're about to post.
            await flushPendingPersists();

            // Finalize every answered question, then end the attempt.
            for (const qaq of questions.value) {
                if (answered(qaq)) await persist(qaq, true);
            }

            await axios.put(`/api/quiz-attempts/${attempt.value.id}`, {
                end_time: endTime,
                user: userId,
                quiz_activity_id: quizActivityId,
            });
            submittedAt.value = endTime;

            // Score/review is best-effort: a disabled-review quiz 403s on /review, but the
            // attempt is already submitted — fall through to the plain "submitted" review.
            if (reviewAllowed) {
                try {
                    await fetchScore(attempt.value.id);
                } catch {
                    score.value = null;
                    reviewQuestions.value = [];
                }
            }

            phase.value = QUIZ_PHASES.REVIEW;
        } catch (e) {
            phase.value = QUIZ_PHASES.ERROR;
            error.value = e;
            throw e;
        }
    }

    async function fetchScore(attemptId) {
        const response = await axios.get(`/api/quiz-attempts/${attemptId}/review`);
        const body = unwrap(response);
        score.value = body?.scoring ?? null;
        reviewQuestions.value = body?.quiz_attempt_questions ?? [];
    }

    function reset() {
        phase.value = QUIZ_PHASES.IDLE;
        error.value = null;
        saveError.value = null;
        attempt.value = null;
        questions.value = [];
        index.value = 0;
        selections.value = {};
        score.value = null;
        reviewQuestions.value = [];
        submittedAt.value = null;
    }

    return {
        // state
        phase,
        error,
        saveError,
        hasError,
        attempt,
        questions,
        index,
        selections,
        score,
        scorePercent,
        correctCount,
        reviewTotal,
        submittedAt,
        hasPastResult,
        blockedByAttempt,
        // derived
        isLaunching,
        isSubmitting,
        started,
        inReview,
        total,
        currentQuestion,
        isFirst,
        isLast,
        // type helpers
        isMultipleAnswer,
        isFreeForm,
        isSelected,
        freeFormText,
        // actions
        start,
        resume,
        select,
        setFreeForm,
        persistDraft,
        next,
        prev,
        submit,
        reset,
    };
}
